import { VaultCodeEnum, getVaultObjectCode } from "./type/dali.type";
import { StoreModel } from "./vault-parser/parser.type";
import { CipherView } from "@bitwarden/common/src/models/view/cipher.view";

import { inject } from "@angular/core";
import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { VaultModelUpgrader } from "@bitwarden/web-vault/app/services/dali/vault.model.upgrader";

export type VaultItemType = Record<string, any>;

export class VaultReader {
  protected cipherService: CipherService = inject(CipherService);
  protected log: LogService = inject(LogService);
  protected vaultModelUpgrader: VaultModelUpgrader = VaultModelUpgrader.getInstance();

  private _allVaultData: Map<VaultCodeEnum, StoreModel[]> | null = null;

  async loadVault(): Promise<Map<VaultCodeEnum, StoreModel[]>> {
    const ciphers: CipherView[] = await this.cipherService.getAllDecrypted();

    const vaultStoreData: Map<VaultCodeEnum, StoreModel[]> = new Map();

    for (const cipher of ciphers) {
      let collections: VaultItemType[] = [];

      try {
        collections = JSON.parse(cipher.notes) as VaultItemType[];
      } catch (e) {
        this.log.error("Could not cast vault content fo JSON");
        this.log.error(e);
      }

      try {
        this.parseCipher(vaultStoreData, collections, cipher);
      } catch (e) {
        this.log.error(`Failed to parse vault data for cipher ${cipher.id}`);
        this.log.error(e);
      }
    }

    this._allVaultData = vaultStoreData;
    return vaultStoreData;
  }

  /**
   * Loads all vault data, caches it, and returns type counts.
   * Subsequent calls to `getDataForType` read from the cache without re-fetching.
   */
  async loadAndCacheVault(): Promise<Map<VaultCodeEnum, number>> {
    const data = await this.loadVault();
    const typeCounts = new Map<VaultCodeEnum, number>();
    for (const [code, items] of data) {
      if (code === VaultCodeEnum.UNKNOWN) {
        continue;
      }
      typeCounts.set(code, items.length);
    }
    return typeCounts;
  }

  /**
   * Returns cached data for a specific type. Must call `loadAndCacheVault` first.
   */
  getDataForType(targetType: VaultCodeEnum): StoreModel[] {
    return this._allVaultData?.get(targetType) ?? [];
  }

  /**
   * Reloads vault data for a specific type from the vault (e.g. after a delete)
   * and updates the cache.
   */
  async reloadDataForType(targetType: VaultCodeEnum): Promise<StoreModel[]> {
    const ciphers: CipherView[] = await this.cipherService.getAllDecrypted();
    const results: StoreModel[] = [];

    for (const cipher of ciphers) {
      const code = getVaultObjectCode(cipher.name);
      if (code !== targetType) {
        continue;
      }

      let collections: VaultItemType[] = [];
      try {
        collections = JSON.parse(cipher.notes) as VaultItemType[];
      } catch (e) {
        this.log.error("Could not cast vault content fo JSON");
        this.log.error(e);
        continue;
      }

      const tempMap = new Map<VaultCodeEnum, StoreModel[]>();
      try {
        this.parseCipher(tempMap, collections, cipher);
      } catch (e) {
        this.log.error(`Failed to reload vault data for cipher ${cipher.id}`);
        this.log.error(e);
        continue;
      }
      const parsed = tempMap.get(targetType);
      if (parsed) {
        results.push(...parsed);
      }
    }

    if (this._allVaultData) {
      this._allVaultData.set(targetType, results);
    }
    return results;
  }

  /**
   * Parses one cipher into `storeData` and logs its skipped items as a single line: a client
   * meeting rows written at a newer `v` (e.g. an old tab open across a deploy) would otherwise log
   * every row of the type. Ids only: an item's contents are user data and must never reach the
   * logs. The first error keeps its stack, which `${error}` would drop.
   */
  private parseCipher(
    storeData: Map<VaultCodeEnum, StoreModel[]>,
    collections: VaultItemType[],
    cipher: CipherView,
  ) {
    const failures = this.vaultModelUpgrader.parseVaultData(storeData, collections, cipher);
    if (failures.length === 0) {
      return;
    }
    const firstError = failures[0].error;
    this.log.error(
      `Skipped ${failures.length} unparseable item(s) in cipher ${cipher.id} ` +
        `(ids: ${failures.map(({ id }) => id || "(no id)").join(", ")}); first error: ` +
        (firstError instanceof Error
          ? (firstError.stack ?? firstError.message)
          : String(firstError)),
    );
  }
}
